import BaseButton from 'components/atoms/baseButton';
import BaseSpinWrapper from 'components/atoms/base-spin-wrapper';
import { CommentType, ModuleType } from 'infrastructure/enums';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { endOfDate, startOfDate } from 'infrastructure/functions';
import useUser from 'utils/useUser';
import { useUpdateInteractionStatus } from 'infrastructure/hooks/control-panel';
import useMonthlyInteractionAlert from 'infrastructure/hooks/utils/use-monthly-interaction-alert';
import classNames from 'classnames';
import { useComment } from 'infrastructure/hooks/comments';
import { sortArrayByFieldName } from 'utils/helpers/sort-array-by-field-name';
import TimeframePicker from 'components/molecules/oneplaform/timeframe-filter/timeframe-picker';

import GeneralCommentsList from './list/index';
import s from './styles.module.scss';
import GeneralCommentsTextArea from './text-area';
import GeneralCommentsEmptyBlock from './empty-block';
import ModuleTabs from '../module-tabs';

import type { Comment } from 'infrastructure/classes/comments/comment';
import type { ReactNode } from 'react';
import type { Dayjs } from 'dayjs';
import type { IGeneralCommentsTextAreaRef } from './text-area';

export type GeneralCommentsMode = 'single' | 'split';

interface IGeneralCommentsProps {
  patientGuid: string;
  className?: string;
  readOnly?: boolean;
  module?: ModuleType;
  interactionStatus?: boolean;
  withSwitcher?: boolean;
  switchModeButton?: ReactNode;
  mode?: GeneralCommentsMode;
  onTabChange?: (m: ModuleType) => void;
}

export interface CommentSectionConfig {
  desktopHeight: number;
  mobileHeight: number;
  bottomPadding: number;
}

export const DEFAULT_CONFIG: CommentSectionConfig = {
  desktopHeight: 609,
  mobileHeight: 409,
  bottomPadding: 74,
};

const GeneralComments: React.FC<IGeneralCommentsProps> = ({
  patientGuid,
  interactionStatus = false,
  module: type = ModuleType.RPM,
  readOnly = false,
  withSwitcher = false,
  switchModeButton,
  className,
  mode,
  onTabChange,
}) => {
  const { t } = useTranslation();
  const { userType, hasClinicalRole, isMobileDevice } = useUser();
  const { MonthlyInteractionAlert, showMonthlyInteractionAlert } =
    useMonthlyInteractionAlert({
      patientGuid,
    });
  const { updateInteractionStatus } = useUpdateInteractionStatus();

  const [module, setModule] = useState<ModuleType>(type);
  const [isExpanded, setIsExpanded] = useState(true);
  const [withFilters, setWithFilters] = useState(false);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
    null,
    null,
  ]);
  const [bottomPadding, setBottomPadding] = useState(
    DEFAULT_CONFIG.bottomPadding,
  );

  const generalCommentsTextAreaRef = useRef<IGeneralCommentsTextAreaRef>(null);
  const isSameModule = mode ? true : type === module;
  let title = t('labels.generalComments', 'General Comments');

  if (mode === 'split') {
    if (module === ModuleType.RPM) {
      title = t('labels.rpmComments', 'RPM Comments');
    } else {
      title = t('labels.ccmComments', 'CCM Comments');
    }
  }

  const {
    data,
    setMeta,
    sendComment,
    deleteComment,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isSending,
    isDeleting,
  } = useComment({
    patientGuid,
    meta: { commentType: module },
  });

  const comments = useMemo(() => {
    const rows =
      data?.pages
        .flatMap((el) => el.data)
        .filter(
          (el) => el.patientGuid === patientGuid && el.commentType === module,
        ) || [];
    return sortArrayByFieldName(rows, 'createdAt');
  }, [data, patientGuid, module]);

  const { desktopHeight, mobileHeight } = DEFAULT_CONFIG;
  const commentsHeight = isMobileDevice ? mobileHeight : desktopHeight;
  const textAreaLoading = isLoading || isSending || isDeleting;

  const handleSendComment = useCallback(
    async (message: string) => {
      if (!message.trim()) return;
      let interactionConfirm = false;

      try {
        if (
          hasClinicalRole &&
          module === ModuleType.RPM &&
          !interactionStatus
        ) {
          interactionConfirm = await showMonthlyInteractionAlert();
        }

        const response = await sendComment({
          commentType: module,
          message,
          patientGuid,
          type: CommentType.Text,
        });

        if (response && interactionConfirm) {
          await updateInteractionStatus({ patientGuid, isInteracted: true });
        }
      } finally {
        generalCommentsTextAreaRef.current?.resetForm();
      }
    },
    [
      module,
      patientGuid,
      interactionStatus,
      hasClinicalRole,
      showMonthlyInteractionAlert,
      sendComment,
      updateInteractionStatus,
      t,
    ],
  );

  const handleDeleteComment = useCallback(
    async (commentToDelete: Comment) => {
      await deleteComment({
        guid: commentToDelete.guid,
        userType,
        patientGuid: commentToDelete.patientGuid,
        commentType: module,
        type: commentToDelete.type as CommentType,
      });
    },
    [deleteComment, module, patientGuid, userType],
  );

  const handleTimeRangeChange = (dates: null | (Dayjs | null)[]) => {
    const start = dates?.[0];
    const end = dates?.[1];

    const startTime = start ? startOfDate(start.toDate()).getTime() : undefined;
    const endTime = end ? endOfDate(end.toDate()).getTime() : undefined;

    setMeta((prev) => ({
      ...prev,
      startTime,
      endTime,
    }));
    setWithFilters(!!(start && end));
    setDateRange([start ?? null, end ?? null]);
  };

  const handleModuleChange = useCallback(
    (m: ModuleType) => {
      setModule(m);
      setMeta((prev) => ({ ...prev, commentType: m }));
      onTabChange?.(m);
    },
    [onTabChange, setMeta],
  );

  useEffect(() => {
    if (type !== module) handleModuleChange(type);
  }, [type]);

  return (
    <BaseSpinWrapper spinning={isLoading}>
      <div className={classNames(s.wrapper, className)}>
        <div className={s.header} data-cy="general-comments-list">
          <div className={s['header-left']}>
            <BaseButton
              type="secondary"
              icon={isExpanded ? <DownOutlined /> : <RightOutlined />}
              onClick={() => setIsExpanded(!isExpanded)}
              className={s['expand-btn']}
            />
            <p className={s.title} data-cy={`general-comment-title-${module}`}>
              {title}
            </p>
          </div>
          <div className={s.controls}>
            <TimeframePicker
              selectedRange={dateRange}
              onRangeChange={handleTimeRangeChange}
              clearSelection={() => handleTimeRangeChange(null)}
              minRangeDays={0}
            />
            {withSwitcher && (
              <ModuleTabs
                activeKey={module}
                onChange={({ key }) => handleModuleChange(key)}
              />
            )}
            {switchModeButton}
          </div>
        </div>
        {isExpanded && (
          <>
            <div className={s.body}>
              {comments.length ? (
                <GeneralCommentsList
                  id={`general-comments-list${module}`}
                  items={comments}
                  hasMore={hasNextPage}
                  loadNext={fetchNextPage}
                  height={commentsHeight - bottomPadding}
                  onDeleteComment={handleDeleteComment}
                />
              ) : (
                <GeneralCommentsEmptyBlock
                  withFilters={withFilters}
                  height={commentsHeight - bottomPadding}
                />
              )}
            </div>
            {!readOnly && isSameModule && (
              <div
                className={s.footer}
                data-cy={`general-comment-input-${module}`}
              >
                <GeneralCommentsTextArea
                  type={module}
                  ref={generalCommentsTextAreaRef}
                  loading={textAreaLoading}
                  onSend={handleSendComment}
                  onResize={({ height }) => setBottomPadding(height)}
                />
              </div>
            )}
          </>
        )}
      </div>
      {MonthlyInteractionAlert}
    </BaseSpinWrapper>
  );
};

export default GeneralComments;
